import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarRange, X } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Switch } from './switch';
import { Button } from './button';
import { currentWeek, rangeToWeek, weekLabel, weekToRange, weeksInYear } from '../../lib/weekFilter';

const NumSelect = ({ value, onChange, options, testId, label }) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span className="text-[11px] font-medium text-gray-500">{label}</span>
    <Select value={value == null ? '' : String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="h-8 text-xs" data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent className="max-h-64">
        {options.map((o) => (
          <SelectItem key={o.value} value={String(o.value)} data-testid={`${testId}-${o.value}`}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const yearOptions = () => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3, y - 4].map((v) => ({ value: v, label: String(v) }));
};

const weekOptions = (year, min = 1) =>
  Array.from({ length: weeksInYear(year) - min + 1 }, (_, i) => ({ value: i + min, label: `W${i + min}` }));

const clamp = (f) => {
  const max = weeksInYear(f.year);
  const from = Math.min(f.from, max);
  return { ...f, from, to: Math.max(from, Math.min(f.to, max)) };
};

const WeekFilterBody = ({ value, onChange, testId }) => {
  const { t } = useTranslation();
  const cw = currentWeek();
  const v = value || { year: cw.year, from: cw.week, to: cw.week };
  const shown = value || { year: null, from: null, to: null };
  const [range, setRange] = useState(!!value && value.to !== value.from);
  const set = (patch) => {
    const next = clamp({ ...v, ...patch });
    onChange(range ? next : { ...next, to: next.from });
  };
  return (
    <div className="space-y-3" data-testid={`${testId}-panel`}>
      <div className="grid grid-cols-2 gap-2">
        <NumSelect label={t('weekFilter.year')} value={shown.year} options={yearOptions()} testId={`${testId}-year`} onChange={(year) => set({ year })} />
        <NumSelect label={range ? t('weekFilter.fromWeek') : t('weekFilter.week')} value={shown.from} options={weekOptions(v.year)} testId={`${testId}-from`} onChange={(from) => set({ from, to: Math.max(from, v.to) })} />
      </div>
      <label className="flex items-center justify-between gap-2 text-xs text-gray-700">
        <span>{t('weekFilter.range')}</span>
        <Switch
          checked={range}
          data-testid={`${testId}-range-toggle`}
          onCheckedChange={(on) => { setRange(on); onChange(on ? v : { ...v, to: v.from }); }}
        />
      </label>
      {range && (
        <NumSelect label={t('weekFilter.toWeek')} value={shown.to} options={weekOptions(v.year, v.from)} testId={`${testId}-to`} onChange={(to) => set({ to })} />
      )}
      <div className="flex flex-wrap gap-1.5 pt-1 border-t">
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`${testId}-this-week`}
          onClick={() => { setRange(false); onChange({ year: cw.year, from: cw.week, to: cw.week }); }}>
          {t('weekFilter.thisWeek')}
        </Button>
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid={`${testId}-last-week`}
          onClick={() => { const p = lastWeek(); setRange(false); onChange({ year: p.year, from: p.week, to: p.week }); }}>
          {t('weekFilter.lastWeek')}
        </Button>
        <Button type="button" size="sm" variant="ghost" className="h-7 text-xs ml-auto" data-testid={`${testId}-clear`}
          onClick={() => onChange(null)} disabled={!value}>
          {t('weekFilter.clear')}
        </Button>
      </div>
    </div>
  );
};

const lastWeek = () => {
  const cw = currentWeek();
  if (cw.week > 1) return { year: cw.year, week: cw.week - 1 };
  return { year: cw.year - 1, week: weeksInYear(cw.year - 1) };
};

/** "Week" filter button: year + ISO week (or W40–W43 range) picker. */
export const WeekFilter = ({ value, onChange, testId = 'week-filter', className = '' }) => {
  const { t } = useTranslation();
  return (
    <div className={`inline-flex items-center flex-shrink-0 ${className}`}>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 text-xs transition-colors ${
              value ? 'border-gray-900 bg-gray-900 text-white hover:bg-gray-800' : 'border-input bg-background text-gray-700 hover:bg-gray-50'
            } ${value ? 'rounded-r-none' : ''}`}
          >
            <CalendarRange className="h-3.5 w-3.5 flex-shrink-0" />
            <span data-testid={`${testId}-label`}>{value ? weekLabel(value) : t('weekFilter.anyWeek')}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-3">
          <WeekFilterBody value={value} onChange={onChange} testId={testId} />
        </PopoverContent>
      </Popover>
      {value && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={t('weekFilter.clear')}
          data-testid={`${testId}-x`}
          className="inline-flex h-8 items-center rounded-r-md border border-l-0 border-gray-900 bg-gray-900 px-1.5 text-white transition-colors hover:bg-gray-700"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};



/** WeekFilter bound to an existing from/to date-range filter. */
export const WeekRangeFilter = ({ from, to, onChange, testId, className }) => (
  <WeekFilter
    value={rangeToWeek(from, to)}
    onChange={(f) => { const r = weekToRange(f); onChange(r.from, r.to); }}
    testId={testId}
    className={className}
  />
);
export default WeekFilter;
